import PropTypes from 'prop-types'
import { bowserFlags } from 'lib/bowser'
import { Col, Row } from 'react-bootstrap'
import { map } from 'lodash'
import classNames from 'classnames'
import * as blUrls from 'lib/urls'
import PageAnimation from 'components/page-animation'
import { filterPostsByTopicSlug } from 'hello_baby/lib'
import SideNav from 'components/side-nav'
import css from './post-list-view.scss'
import {
  getActiveSlugSet,
  getTopicNameForSlug,
  getTopicDescriptionForSlug,
} from '../../reducers'
import PregnancyWeekSelect from '../pregnancy-week-select'
import { SectionHeading } from '../../../components/headings'
import { track, useTracking } from 'lib/analytics'
import useOnCurrentUserLoaded from 'shared/hooks/useOnCurrentUserLoaded/useOnCurrentUserLoaded'
import {
  getSearchHistoryOfType,
  SEARCH_HISTORY_TYPE_GUIDES,
} from 'shared/utils/searchHistory/searchHistory'
import {
  GUIDE_SEARCH_INDEX_KEY,
  getSearchId,
  useSearchId,
} from 'shared/hooks/useSearchId/useSearchId'
import removeQueryParam from 'shared/utils/removeQueryParam/removeQueryParam'
import getSearchResultsPageEventType from 'shared/helpers/getSearchResultsPageEventType'
import { useSelector } from 'react-redux'

const PostListView = ({
  title,
  topicNavigation,
  description,
  children,
  searchResults,
  location,
}) => {
  const state = useSelector((state) => state)
  const topicSlug = location.pathname.split('/').slice(-1)[0]
  const posts = filterPostsByTopicSlug(state.posts, topicSlug, {
    sortBy: 'title',
    order: 'asc',
  })

  const tracker = useTracking()
  const [_, getSearchId] = useSearchId(GUIDE_SEARCH_INDEX_KEY)

  title = title || getTopicNameForSlug(topicSlug, state)
  description = description || getTopicDescriptionForSlug(topicSlug, state)

  useOnCurrentUserLoaded((currentUser) => {
    // This page is used for category lists as well as search results. We don't want this event to fire for category lists.
    // Search results will be undefined if this is a category list, otherwise it will be an array.
    if (!Array.isArray(searchResults)) return

    // Remove the single quotes in the title string on search results.
    const cleanSearchTerm = title.replace(/["']/g, '')
    const lastSearchTerm = getSearchHistoryOfType(SEARCH_HISTORY_TYPE_GUIDES)[0]

    tracker.trackEvent({
      event: track.searchResultsPageViewed,
      eventLocation: track.EventLocation.GUIDES,
      eventType: getSearchResultsPageEventType(track.EventType.GUIDES),
      eventUrl: window.location.href,
      registryOwnerId: currentUser?.currentRegistry?.ownerId,
      registryId: currentUser?.registryId,
      isRegistryOwner:
        currentUser &&
        currentUser?.currentRegistry?.ownerId === currentUser?.id,
      listType:
        currentUser?.currentRegistry?.type || track.ListType.BABY_REGISTRY,
      searchTerm: cleanSearchTerm,
      searchId: getSearchId(),
      resultCount: searchResults.length,
      top20Results: searchResults.slice(0, 20).map((r) => r.slug),
      executedSearch: lastSearchTerm === cleanSearchTerm || false,
    })
  })

  const navCategory = { title: 'Home', path: '/hello-baby', urlSlug: null }
  const sideNavTree = map(topicNavigation.items, (topic) => ({
    title: topic.name,
    urlSlug: topic.slug,
    children: map(topic.children, (topic) => ({
      title: topic.name,
      urlSlug: topic.slug,
      path: blUrls.helloBabyPostPath(topic.slug),
    })),
    path: blUrls.helloBabyPostPath(topic.slug),
  }))

  return (
    <div className="container">
      <Row>
        <Col
          key={`${topicSlug}-header`}
          sm={12}
          className={classNames(
            css.titleContainer,
            'flex-vertical-center-sm mvl'
          )}
        >
          <div className="full-width text-center">
            <SectionHeading>{title}</SectionHeading>
            {description ? <div className="h5">{description}</div> : null}
            {topicSlug === 'pregnancy-week-by-week' ? (
              <PregnancyWeekSelect posts={posts} className="inline-block" />
            ) : null}
          </div>
        </Col>
        {!bowserFlags.mobile && (
          <Col sm={3} className={css.sidenav}>
            <SideNav
              sidebarTitle="Guides"
              navCategory={navCategory}
              categoryTree={sideNavTree || []}
              activeSlugSet={getActiveSlugSet(topicSlug, topicNavigation)}
            />
          </Col>
        )}
        <Col sm={9} className="ptm mts">
          <PageAnimation>
            <div key={topicSlug} className={css.contentContainer}>
              {children}
            </div>
          </PageAnimation>
        </Col>
      </Row>
    </div>
  )
}

PostListView.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  topicNavigation: PropTypes.object.isRequired,
  emailUrls: PropTypes.object,
}

export default PostListView
